import React, { useCallback, useEffect, useMemo, useRef } from "react";
import type { Employee, Schedule, Shift } from "@/types";
import { AVAILABILITY_CONFIG, formatShiftTime, formatShortName, getShiftColor, getShiftDisplayContent } from "@/utils/schedulerUtils";
import ShiftBreachTooltip from "@/components/tooltips/ShiftBreachTooltip";
import { format, max, min, parseISO } from "date-fns";
import { useShiftTooltip } from "@/hooks/useShiftTooltip";
import CalendarModalShell from "./CalendarModalShell";
import type { ShellDayCell } from "./CalendarModalShell.types";

interface CalendarModalProps {
    open: boolean;
    onClose: () => void;
    title: string;
    filteredShifts: Shift[];
    schedule: Schedule;
    problematicShiftIds?: string[];
    shiftConstraintBreaches?: { [shiftId: string]: string[] };
    periodStartDate?: string;
    periodEndDate?: string;
    badgeContentType: "role" | "employee" | "roleEmployee";
    employee?: Employee;
    // Multi-employee variant: entries get prefixed with the person's name.
    availabilityEmployees?: Employee[];
    availabilityTypes?: AvailabilityType[];
    headerActions?: React.ReactNode;
    emptyWhenNoShifts?: boolean;
    emptyLabel?: string;
    // Hovering an entry outlines the same person's other entries of that role or availability type.
    highlightRelatedOnHover?: boolean;
    onShiftClick?: (shiftId: string) => void;
    onPrev?: () => void;
    onNext?: () => void;
    hasPrev?: boolean;
    hasNext?: boolean;
}

type AvailabilityType = "unavailable" | "undesired" | "desired";

const ALL_AVAILABILITY_TYPES: AvailabilityType[] = ["unavailable", "undesired", "desired"];

interface AvailabilityItem {
    id: string;
    type: AvailabilityType;
    start: string;
    end: string;
    employeeId: string;
    // Only set when several employees share the calendar, to tell their entries apart.
    employeeName?: string;
}

// A badge splits its width between the role and the person, so the calendar shows initials only
// ("Meriem Boufassa" -> "M. B"); compound surnames keep one initial per part ("V. M-C"). The
// hover tooltip carries the full name when the initials are ambiguous.
const toInitials = (fullName: string): string => {
    const parts = fullName.trim().split(/\s+/);
    const surname = parts.slice(1).join(" ");

    if (!parts[0] || !surname) {
        return fullName;
    }

    return `${parts[0].charAt(0).toUpperCase()}. ${surname.replace(/(\p{L})[\p{L}'’]*/gu, "$1").toUpperCase()}`;
};

const ShiftBadge: React.FC<{
    shift: Shift;
    schedule: Schedule;
    isProblematic: boolean;
    onMouseEnter: (e: React.MouseEvent) => void;
    onMouseLeave: () => void;
    onClick: (e: React.MouseEvent) => void;
    onFocus: (e: React.FocusEvent) => void;
    onBlur: () => void;
    contentType: "role" | "employee" | "roleEmployee";
    highlightGroup?: string;
}> = ({ shift, schedule, isProblematic, onMouseEnter, onMouseLeave, onClick, onFocus, onBlur, contentType, highlightGroup }) => {
    const employee = shift.employee
        ? schedule.employees.find(e => e.userId === shift.employee?.userId)
        : undefined;

    const color = getShiftColor(shift, employee);
    const roleLabel = shift.roleName || shift.requiredSkill || '';
    const splitLabels = contentType === "roleEmployee" && !!roleLabel;
    const employeeLabel = splitLabels && shift.employee
        ? toInitials(shift.employee.name)
        : getShiftDisplayContent(shift);
    let content: string;
    if (contentType === "employee" || contentType === "roleEmployee") {
        content = employeeLabel;
    } else {
        content = roleLabel;
    }
    const border = isProblematic ? '2px solid #fc0505' : 'none';

    return (
        <button
            type="button"
            className="flex items-baseline gap-1 overflow-hidden whitespace-nowrap cursor-pointer text-left w-full text-inherit text-sm border-none"
            style={{
                backgroundColor: color,
                borderRadius: "8px",
                padding: "5px",
                border
            }}
            data-highlight-group={highlightGroup}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onClick={onClick}
            onFocus={onFocus}
            onBlur={onBlur}
        >
            {splitLabels ? (
                <>
                    <span className="truncate">{roleLabel}</span>
                    {/* Never shrinks, so the person stays partly readable however long the role is. */}
                    <span className="truncate shrink-0 max-w-[50%]">- {employeeLabel}</span>
                </>
            ) : (
                <span className="truncate">{content}</span>
            )}
        </button>
    );
};

const getSlotsForDate = (
    dateStr: string,
    slots: { start: string; end: string }[] | undefined,
    type: AvailabilityType,
    employeeId: string,
    employeeName?: string,
): AvailabilityItem[] => {
    if (!slots) {
        return [];
    }

    const dayStart = new Date(dateStr + "T00:00:00").getTime();
    const dayEnd = new Date(dateStr + "T23:59:59").getTime();

    return slots
        .filter(slot => {
            const s = new Date(slot.start).getTime();
            const e = new Date(slot.end).getTime();
            return s < dayEnd && e > dayStart;
        })
        .map((slot, idx) => ({
            id: `${employeeId}-${type}-${dateStr}-${idx}`,
            type,
            start: slot.start,
            end: slot.end,
            employeeId,
            employeeName,
        }));
};

const SLOTS_BY_TYPE: Record<AvailabilityType, (employee: Employee) => { start: string; end: string }[] | undefined> = {
    unavailable: (employee) => employee.unavailableTimeSlots,
    undesired: (employee) => employee.undesiredTimeSlots,
    desired: (employee) => employee.desiredTimeSlots,
};

const getAvailabilitiesForDate = (
    dateStr: string,
    employees: Employee[],
    types: AvailabilityType[],
    withNames: boolean,
): AvailabilityItem[] => employees.flatMap(employee =>
    types.flatMap(type => getSlotsForDate(
        dateStr,
        SLOTS_BY_TYPE[type](employee),
        type,
        employee.userId,
        withNames ? formatShortName(employee.name) : undefined,
    ))
);

const CalendarModal: React.FC<CalendarModalProps> = (
    {
        open,
        onClose,
        title,
        filteredShifts,
        schedule,
        problematicShiftIds,
        shiftConstraintBreaches,
        periodStartDate,
        periodEndDate,
        badgeContentType,
        employee,
        availabilityEmployees,
        availabilityTypes,
        headerActions,
        emptyWhenNoShifts,
        emptyLabel = "Aucun shift trouvé.",
        highlightRelatedOnHover,
        onShiftClick,
        onPrev,
        onNext,
        hasPrev,
        hasNext,
    }) => {
    const {
        shiftTooltip,
        containerRef,
        handleShiftMouseEnter,
        handleShiftMouseLeave,
        handleShiftClick,
        handleGenericMouseEnter,
    } = useShiftTooltip(schedule, shiftConstraintBreaches);

    // One CSS rule outlines every matching badge without threading hover state through the day cells.
    const highlightStyleRef = useRef<HTMLStyleElement>(null);
    const highlightedBadgeRef = useRef<HTMLElement | undefined>(undefined);
    const highlightLike = useCallback((badge?: HTMLElement) => {
        highlightedBadgeRef.current = badge;
        const group = badge?.dataset.highlightGroup;

        if (highlightStyleRef.current) {
            highlightStyleRef.current.textContent = group
                ? `[data-highlight-group="${CSS.escape(group)}"] { outline: 3px solid #111827; outline-offset: -1px; }`
                : "";
        }
    }, []);

    // The hovered badge can be reassigned or filtered out without the pointer ever leaving it.
    useEffect(() => {
        const badge = highlightedBadgeRef.current;
        highlightLike(badge?.isConnected ? badge : undefined);
    }, [filteredShifts, availabilityEmployees, availabilityTypes, highlightLike]);

    const { startStr, endStr } = useMemo(() => {
        // Shifts are only needed to infer the bounds; explicit ones still let the grid render
        // an empty schedule, which availabilities rely on.
        if (schedule.shifts.length === 0 && !(periodStartDate && periodEndDate)) {
            return { startStr: "", endStr: "" };
        }

        const minDate = periodStartDate
            ? parseISO(periodStartDate)
            : min(schedule.shifts.map(s => parseISO(s.start)));
        const maxDate = periodEndDate
            ? parseISO(periodEndDate)
            : max(schedule.shifts.map(s => parseISO(s.end)));

        return {
            startStr: format(minDate, "yyyy-MM-dd"),
            endStr: format(maxDate, "yyyy-MM-dd"),
        };
    }, [schedule.shifts, periodStartDate, periodEndDate]);

    const shiftsByDate = useMemo(() => {
        const map = new Map<string, Shift[]>();
        for (const shift of filteredShifts) {
            const dateStr = format(new Date(shift.start), "yyyy-MM-dd");
            const bucket = map.get(dateStr);
            if (bucket) {
                bucket.push(shift);
            } else {
                map.set(dateStr, [shift]);
            }
        }
        return map;
    }, [filteredShifts]);

    const availabilitySources = useMemo(
        () => availabilityEmployees ?? (employee ? [employee] : []),
        [availabilityEmployees, employee]
    );
    const shownAvailabilityTypes = availabilityTypes ?? ALL_AVAILABILITY_TYPES;
    const namedAvailabilities = !!availabilityEmployees;

    const availabilityCache = useMemo(
        () => new Map<string, AvailabilityItem[]>(),
        [availabilitySources, shownAvailabilityTypes, namedAvailabilities]
    );

    const renderDayCell = useCallback((cell: ShellDayCell) => {
        const dayShifts = shiftsByDate.get(cell.dateStr) ?? [];

        let availabilities = availabilityCache.get(cell.dateStr);
        if (!availabilities) {
            availabilities = getAvailabilitiesForDate(
                cell.dateStr,
                availabilitySources,
                shownAvailabilityTypes,
                namedAvailabilities
            );
            availabilityCache.set(cell.dateStr, availabilities);
        }

        return (
            <div
                className="h-full min-h-[120px] p-2"
                style={cell.isHoliday ? { backgroundColor: "var(--holiday-bg)" } : undefined}
            >
                <div className="text-gray-600 mb-2">
                    {cell.dayLabel}
                </div>
                <div className="space-y-1">
                    {dayShifts.map((shift) => {
                        const isProblematic = problematicShiftIds?.includes(shift.id) || false;
                        const highlightGroup = highlightRelatedOnHover && shift.employee
                            ? `shift|${shift.employee.userId}|${shift.roleName || shift.requiredSkill || ""}`
                            : undefined;
                        const showShift = (e: React.MouseEvent | React.FocusEvent) => {
                            highlightLike(e.currentTarget as HTMLElement);
                            handleShiftMouseEnter(shift.id, e);
                        };
                        const hideShift = () => {
                            highlightLike();
                            handleShiftMouseLeave();
                        };
                        return (
                            <ShiftBadge
                                key={shift.id}
                                shift={shift}
                                schedule={schedule}
                                isProblematic={isProblematic}
                                onMouseEnter={showShift}
                                onMouseLeave={hideShift}
                                onClick={onShiftClick
                                    ? () => onShiftClick(shift.id)
                                    : (e) => handleShiftClick(shift.id, e)}
                                onFocus={showShift}
                                onBlur={hideShift}
                                contentType={badgeContentType}
                                highlightGroup={highlightGroup}
                            />
                        );
                    })}
                    {availabilities
                        .toSorted((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())
                        .map((item) => {
                            const config = AVAILABILITY_CONFIG[item.type];
                            const timeRange = `${formatShiftTime(item.start)} - ${formatShiftTime(item.end)}`;
                            const label = item.employeeName ? `${config.label} - ${item.employeeName}` : config.label;
                            const highlightGroup = highlightRelatedOnHover
                                ? `${item.type}|${item.employeeId}`
                                : undefined;
                            const showItem = (e: React.MouseEvent | React.FocusEvent) => {
                                highlightLike(e.currentTarget as HTMLElement);
                                handleGenericMouseEnter({
                                    shiftId: item.id,
                                    lines: [],
                                    timeRange,
                                    employeeName: item.employeeName || title,
                                    roleName: config.label,
                                }, e);
                            };
                            const hideItem = () => {
                                highlightLike();
                                handleShiftMouseLeave();
                            };
                            return (
                                <button
                                    key={item.id}
                                    type="button"
                                    className="truncate cursor-default text-left w-full text-inherit text-sm border-none"
                                    style={{
                                        backgroundColor: config.bg,
                                        opacity: 0.5,
                                        borderRadius: "8px",
                                        padding: "5px",
                                    }}
                                    data-highlight-group={highlightGroup}
                                    onMouseEnter={showItem}
                                    onMouseLeave={hideItem}
                                    onFocus={showItem}
                                    onBlur={hideItem}
                                >
                                    {label}
                                </button>
                            );
                        })}
                </div>
            </div>
        );
    }, [
        shiftsByDate,
        availabilityCache,
        availabilitySources,
        shownAvailabilityTypes,
        namedAvailabilities,
        problematicShiftIds,
        schedule,
        badgeContentType,
        title,
        handleShiftMouseEnter,
        handleShiftMouseLeave,
        handleShiftClick,
        handleGenericMouseEnter,
        onShiftClick,
        highlightRelatedOnHover,
        highlightLike,
    ]);

    return (
        <CalendarModalShell
            open={open}
            onClose={onClose}
            title={title}
            startDate={startStr}
            endDate={endStr}
            holidays={schedule.holidays}
            onPrev={onPrev}
            onNext={onNext}
            hasPrev={hasPrev}
            hasNext={hasNext}
            headerActions={headerActions}
            renderDayCell={renderDayCell}
            emptyLabel={emptyLabel}
            isEmpty={!!emptyWhenNoShifts && filteredShifts.length === 0}
            containerRef={containerRef}
            overlay={(
                <>
                    {highlightRelatedOnHover && <style ref={highlightStyleRef}/>}
                    {shiftTooltip && (
                        <ShiftBreachTooltip
                            x={shiftTooltip.x}
                            y={shiftTooltip.y}
                            lines={shiftTooltip.lines}
                            shiftId={shiftTooltip.shiftId}
                            containerRef={containerRef}
                            timeRange={shiftTooltip.timeRange}
                            employeeName={shiftTooltip.employeeName}
                            roleName={shiftTooltip.roleName}
                        />
                    )}
                </>
            )}
        />
    );
};

export default CalendarModal;
